import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { Topbar } from "../components/shell/Topbar";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

interface Project {
  id: string;
  name: string;
  description: string | null;
  color?: string;
}

interface Activity {
  id: string;
  kind: string;
  title: string;
  meta: Record<string, unknown> | null;
  createdAt: string;
  actor?: { name: string; email: string } | null;
}

interface Member {
  id: string;
  role: string;
  user: { id: string; name: string; email: string };
}

interface Stats {
  releasesThisMonth: number;
  activeFlags: number;
  mocks: number;
  webhooks24h: number;
}

interface DraftRelease {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  commitCount: number;
}

const KIND_TONES: Record<string, { label: string; color: string }> = {
  release: { label: "Release", color: "var(--color-signal-red)" },
  flag: { label: "Flag", color: "var(--color-signal-ok)" },
  mock: { label: "Mock", color: "var(--color-signal-warn)" },
  webhook: { label: "Webhook", color: "var(--color-signal-lavender)" },
  audience: { label: "Audience", color: "var(--color-signal-red)" },
  environment: { label: "Env", color: "var(--color-signal-ok)" },
  member: { label: "Member", color: "var(--color-signal-lavender)" },
};

function formatRelative(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

export function ProjectOverviewPage() {
  const { teamId, projectId } = useParams();
  const { teams } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const [project, setProject] = useState<Project | null>(null);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [draftReleases, setDraftReleases] = useState<DraftRelease[]>([]);
  const [filter, setFilter] = useState<string | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !teamId) return;
    fetch(`/api/teams/${teamId}/projects/${projectId}`)
      .then((r) => r.json())
      .then(setProject);
  }, [teamId, projectId]);

  useEffect(() => {
    if (!projectId) return;
    setActivityLoading(true);
    const url = filter
      ? `/api/projects/${projectId}/activity?kind=${filter}&limit=30`
      : `/api/projects/${projectId}/activity?limit=30`;
    fetch(url)
      .then((r) => r.json())
      .then(setActivity)
      .finally(() => setActivityLoading(false));
  }, [projectId, filter]);

  useEffect(() => {
    if (!teamId) return;
    setMembersLoading(true);
    fetch(`/api/teams/${teamId}/members`)
      .then((r) => r.json())
      .then(setMembers)
      .finally(() => setMembersLoading(false));
  }, [teamId]);

  useEffect(() => {
    if (!projectId) return;
    Promise.all([
      fetch(`/api/projects/${projectId}/releases`).then((r) => r.json()),
      fetch(`/api/projects/${projectId}/flags`).then((r) => r.json()),
      fetch(`/api/projects/${projectId}/mocks`).then((r) => r.json()),
      fetch(`/api/projects/${projectId}/webhooks`).then((r) => r.json()),
    ]).then(
      ([releases, flags, mocks, webhooks]: [
        DraftRelease[],
        Array<{ enabled: boolean }>,
        unknown[],
        unknown[],
      ]) => {
        const monthStart = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          1,
        );
        setStats({
          releasesThisMonth: releases.filter(
            (r) => new Date(r.createdAt) >= monthStart,
          ).length,
          activeFlags: flags.filter((f) => f.enabled).length,
          mocks: mocks.length,
          webhooks24h: webhooks.length,
        });
        setDraftReleases(
          releases
            .filter((r) => r.status === "draft")
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            ),
        );
      },
    );
  }, [projectId]);

  if (!team) return null;

  return (
    <>
      <Topbar
        eyebrow="Project overview"
        title={project?.name ?? "…"}
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          { label: project?.name ?? "…" },
        ]}
        actions={
          <>
            <Link to={`/teams/${teamId}/projects/${projectId}/settings`}>
              <Button size="sm" variant="outline">
                Project settings
              </Button>
            </Link>
            <Link to={`/teams/${teamId}/projects/${projectId}/releases`}>
              <Button size="sm" variant="outline">
                + New release
              </Button>
            </Link>
          </>
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 border-b border-rule bg-card-paper">
        {[
          {
            label: "Releases this month",
            value: stats?.releasesThisMonth,
            color: "var(--color-signal-red)",
          },
          {
            label: "Active flags",
            value: stats?.activeFlags,
            color: "var(--color-signal-ok)",
          },
          {
            label: "Mocks live",
            value: stats?.mocks,
            color: "var(--color-signal-warn)",
          },
          {
            label: "Webhooks (active)",
            value: stats?.webhooks24h,
            color: "var(--color-signal-lavender)",
          },
        ].map((s, i, arr) => (
          <div
            key={s.label}
            className={`p-3 sm:p-5 ${i < arr.length - 1 ? "md:border-r" : ""} border-rule`}
          >
            <div className="flex items-center gap-1.5 text-eyebrow">
              <span
                className="size-1.5 rounded-sm"
                style={{ background: s.color }}
              />
              {s.label}
            </div>
            {stats === null ? (
              <Skeleton className="h-6 sm:h-9 md:h-10 w-10 mt-1 sm:mt-1.5" />
            ) : (
              <div className="text-xl sm:text-3xl md:text-4xl font-semibold tracking-tight tabular-nums mt-1 sm:mt-1.5">
                {s.value}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="lg:flex-1 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] lg:overflow-hidden">
        {/* Activity feed */}
        <div className="border-b lg:border-b-0 lg:border-r border-rule px-6 py-5 lg:overflow-auto">
          <div className="flex justify-between items-baseline mb-4">
            <h2 className="text-lg font-semibold tracking-tight">
              Project activity
            </h2>
            <div className="flex gap-3 font-mono text-xs">
              <button
                onClick={() => setFilter(null)}
                className={!filter ? "text-foreground font-bold" : "text-faint"}
              >
                All
              </button>
              {(["release", "flag", "mock", "webhook"] as const).map((k) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={
                    filter === k ? "text-foreground font-bold" : "text-faint"
                  }
                >
                  {KIND_TONES[k]?.label}
                </button>
              ))}
            </div>
          </div>

          {activityLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-[80px_1fr_60px] gap-3 py-3 border-b border-rule-soft items-center"
              >
                <Skeleton className="h-3 w-16" />
                <div className="space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-3 w-10 ml-auto" />
              </div>
            ))
          ) : activity.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-6">
              No activity yet. Toggle a flag or send a release to see entries
              here.
            </div>
          ) : (
            activity.map((a) => {
              const tone =
                KIND_TONES[a.kind] ??
                ({ label: a.kind, color: "var(--color-faint)" } as const);
              return (
                <div
                  key={a.id}
                  className="grid grid-cols-[80px_1fr_60px] gap-3 py-3 border-b border-rule-soft items-center"
                >
                  <div
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider"
                    style={{ color: tone.color }}
                  >
                    <span
                      className="size-1.5 rounded-sm"
                      style={{ background: tone.color }}
                    />
                    {tone.label}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{a.title}</div>
                    {a.actor && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {a.actor.name}
                      </div>
                    )}
                  </div>
                  <div className="font-mono text-xs text-faint text-right">
                    {formatRelative(a.createdAt)}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right rail */}
        <div className="px-6 py-5 lg:overflow-auto bg-panel/40 space-y-4">
          {/* Members — compact strip on mobile */}
          <Link
            to={`/teams/${teamId}/settings`}
            className="md:hidden flex items-center gap-2 bg-card-paper border border-rule rounded-lg px-3 py-2 hover:bg-panel/40"
          >
            {membersLoading ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              <>
                <div className="flex -space-x-1.5">
                  {members.slice(0, 4).map((m) => (
                    <span
                      key={m.id}
                      className="size-5 rounded-full bg-signal-lavender text-paper text-[9px] font-bold flex items-center justify-center ring-1 ring-card-paper"
                      title={m.user.name}
                    >
                      {(m.user.name || m.user.email)
                        .slice(0, 2)
                        .toUpperCase()}
                    </span>
                  ))}
                </div>
                <span className="text-xs flex-1">
                  Team · {members.length} member
                  {members.length === 1 ? "" : "s"}
                </span>
                <span className="font-mono text-xs text-signal-red">→</span>
              </>
            )}
          </Link>

          {/* Members — full card on tablet/desktop */}
          <div className="hidden md:block bg-card-paper border border-rule rounded-lg overflow-hidden">
            <div className="px-4 py-3 flex justify-between items-center border-b border-rule-soft">
              <span className="text-eyebrow">
                Team members{membersLoading ? "" : ` · ${members.length}`}
              </span>
              <Link
                to={`/teams/${teamId}/settings`}
                className="font-mono text-xs text-signal-red"
              >
                Invite →
              </Link>
            </div>
            <div className="px-4 py-3 space-y-2">
              {membersLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Skeleton className="size-6 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-32" />
                      <Skeleton className="h-2.5 w-16" />
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {members.slice(0, 6).map((m) => (
                    <div key={m.id} className="flex items-center gap-2.5">
                      <span className="size-6 rounded-full bg-signal-lavender text-paper text-[10px] font-bold flex items-center justify-center">
                        {(m.user.name || m.user.email).slice(0, 2).toUpperCase()}
                      </span>
                      <div className="flex-1 text-sm">
                        <div className="font-semibold truncate">
                          {m.user.name}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground capitalize">
                          {m.role}
                        </div>
                      </div>
                    </div>
                  ))}
                  {members.length > 6 && (
                    <div className="font-mono text-xs text-faint">
                      +{members.length - 6} more
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="bg-card-paper border border-rule rounded-lg p-4">
            <div className="text-eyebrow mb-2">Releases</div>
            <div className="font-serif text-base font-semibold mb-1">
              Drafts ready for review
            </div>
            {stats === null ? (
              <div className="mt-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ) : draftReleases.length === 0 ? (
              <>
                <div className="text-xs text-muted-foreground mb-3">
                  AI drafts from your repo commits, ready to ship.
                </div>
                <Link to={`/teams/${teamId}/projects/${projectId}/releases`}>
                  <Button size="sm" variant="outline">
                    Open releases
                  </Button>
                </Link>
              </>
            ) : (
              <div className="mt-3 space-y-1">
                {draftReleases.slice(0, 5).map((r) => (
                  <Link
                    key={r.id}
                    to={`/teams/${teamId}/projects/${projectId}/releases/${r.id}`}
                    className="block px-2 py-1.5 rounded hover:bg-panel/50"
                  >
                    <div className="text-sm font-medium truncate">
                      {r.title || "Untitled draft"}
                    </div>
                    <div className="font-mono text-[10px] text-faint mt-0.5">
                      {r.commitCount} commits ·{" "}
                      {format(new Date(r.createdAt), "MMMM do 'of' yyyy")}
                    </div>
                  </Link>
                ))}
                {draftReleases.length > 5 && (
                  <Link
                    to={`/teams/${teamId}/projects/${projectId}/releases`}
                    className="block px-2 py-1.5 text-xs text-signal-red font-semibold"
                  >
                    + {draftReleases.length - 5} more drafts →
                  </Link>
                )}
              </div>
            )}
          </div>

          <div className="bg-card-paper border border-rule rounded-lg p-4">
            <div className="text-eyebrow mb-2">Webhook inbox</div>
            <div className="font-serif text-base font-semibold mb-1">
              Live request log
            </div>
            <div className="text-xs text-muted-foreground mb-3">
              Capture, replay and bypass requests for debugging.
            </div>
            <Link to={`/teams/${teamId}/projects/${projectId}/webhooks`}>
              <Button size="sm" variant="outline">
                Open inbox
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
